/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2017
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { createError } from './errors.js';
import { extractIdObject } from './microdata-to-id-hash.js';
import { convertIdMicrodataToObject } from './microdata-to-object.js';
import { isVersionedObject } from './object-recipes.js';
import { convertObjToIdMicrodata, convertObjToMicrodata } from './object-to-microdata.js';
import { reverseMapUpdater, reverseMapUpdaterForIdObject } from './reverse-map-updater.js';
import { CREATION_STATUS, STORAGE } from './storage-base-common.js';
import { setIdHash } from './storage-id-hash-cache.js';
import { getObject, getObjectWithType, storeUnversionedObject } from './storage-unversioned-objects.js';
import { createCryptoHash } from './system/crypto-helpers.js';
import { exists, readUTF8TextFile, writeUTF8SystemMapFile, writeUTF8TextFile } from './system/storage-base.js';
import { cloneOneObject } from './util/clone-one-object.js';
import { logCall } from './util/object-io-statistics.js';
import { calculateHashOfObj, calculateIdHashOfObj } from './util/object.js';
import { createEventSource } from './util/one-event-source.js';
import { serializeWithType } from './util/promise.js';
import { ensureHash, isHash, isUnversionedObjectResult, isVersionNode } from './util/type-checks.js';
import { mergeObjects } from './crdts/merge-objects.js';
const versionedObjEvent = createEventSource();
export const onVersionedObj = versionedObjEvent.consumer;
/** @internal */
export const idObjEvent = createEventSource();
export const onIdObj = idObjEvent.consumer;
/**
 * @async
 * @param {OneIdObjectTypes} obj - An ID object of a versioned object type, i.e. it has only
 * ID properties (`isId` is `true` for the property in the recipe)
 * @returns {Promise<IdFileCreation>} Returns an {@link IdFileCreation} object
 */
export async function storeIdObject(obj) {
    logCall('storeIdObject', obj.$type$);
    const idObjMicrodata = convertObjToIdMicrodata(obj);
    const idHash = (await createCryptoHash(idObjMicrodata));
    const result = { idHash, status: await writeUTF8TextFile(idObjMicrodata, idHash) };
    await reverseMapUpdaterForIdObject(obj, result);
    idObjEvent.dispatch(result);
    return result;
}
/**
 * Returns the ID object for the given ID hash. Those are stored under the idHash as filename
 * in the 'objects' storage space. There can be "empty" versioned objects for which only an ID
 * object exists but no concrete versions yet, so that it is possible to ID-hash-link to ID
 * objects without existing versions and still be able to find out the concrete ID properties
 * which lead to the given ID hash.
 *
 * @async
 * @param {SHA256IdHash} idHash - Hash of a ONE ID object
 * @returns {Promise<OneIdObjectTypes>} The promise is rejected with an Error whose name
 * property is set to "FileNotFoundError" if the object does not exist.
 */
export async function getIdObject(idHash) {
    const idObjMicrodata = await readUTF8TextFile(idHash);
    const obj = convertIdMicrodataToObject(idObjMicrodata);
    logCall('getIdObject', obj.$type$);
    return obj;
}
/**
 * Object writing policy:
 * a) If the exact same object already exists do nothing, just return the hash of the object.
 * b) If a version of the object exists (based on ID-object) but not exactly the same, create
 *    a new Object. Update the version map.
 * c) If there is no such object yet (based on ID-object), create a new object and its
 *    accompanying Object-map.
 * In any case, always return both hashes for the object and the id-object.
 *
 * 1 Calculate object-ID (hash) from the object (obj => objID)
 *   The object's id hash also is the name of the version map hash
 * 2 Store object (obj => hash)
 * 3 Attempt to update the version map. Its filename is fixed based on the empty version map and
 * the
 *   respective ID-object hash. If this succeeds we know the map and therefore previous
 *   version(s) of the object exist. The promise rejects when the version map does not exist.
 * IF version map does NOT exist:
 *   4 Create a new version map.
 * END
 *
 * @internal
 * @async
 * @param {OneVersionedObjectTypes} obj - A versioned ONE object.
 * @param {string} microdata - When the microdata for the object is already known it can be
 * provided as another parameter, which saves the object-to-microdata conversion otherwise necessary
 * @param {SHA256Hash} hash - When the hash for the microdata is already known it can be
 * provided as another parameter, which saves the object-to-microdata conversion otherwise necessary
 * @returns {Promise<VersionedObjectResult>} Resolves with the crypto hashes of the object
 * and of the accompanying (virtual) ID- and (real) object. The "status" is CREATION_STATUS.NEW
 * if this particular object is new, CREATION_STATUS.EXISTS if it already exists.
 */
export async function storeVersionedObjectWithMicrodataNoMerge(obj, microdata, hash, skipDispatch = false) {
    if (!isVersionedObject(obj)) {
        throw createError('SVO-SO2', { obj });
    }
    // This is the ID hash of the object, not the hash of the object. The version map that
    // stores the hashes of all versions of this object is stored using this hash as map name.
    const objId = (await createCryptoHash(
    // This is a string: We know we have a versioned object, so we will not get undefined
    extractIdObject(microdata)));
    async function serializedStore() {
        await storeIdObject(obj);
        // THE MAIN STEP. If this object already exists the promise will be *rejected* with an
        // error, so subsequent "then()" steps chained to this one below are not executed. We don't
        // check for existence of the object because we subscribe to the node.js philosophy of not
        // adding a useless I/O call when we get the exact same information when we try to write to
        // it anyway. We don't tell storeUTF8Clob() to ignore EEXIST errors because when an object
        // already exists we don't want to update the version map, but we simply do nothing.
        const status = await writeUTF8TextFile(microdata, hash);
        // Optional, an optimization. If we don't put it into the cache, and it is queried, it would
        // have to be loaded from storage and calculated. We assume that there is a significant
        // probability that when we store a versioned object its ID hash will be needed very soon,
        // for example to write reverse maps when shortly hereafter another object is written
        // referencing this one.
        setIdHash(hash, objId);
        // The reverse and version map updates are independent and can be run in parallel.
        await reverseMapUpdater(obj, { hash, status });
        // We need to copy the object in order to prevent that modifications on the
        // returned value will modifiy the original
        return {
            obj: cloneOneObject(obj),
            hash,
            idHash: objId,
            status,
            timestamp: Date.now()
        };
    }
    const result = await serializeWithType(`ID ${objId}`, serializedStore);
    // Dispatch event for new objects (including CHUM imports)
    // Skip dispatch when called internally - caller (mergeVersionWithCurrent) handles dispatch
    // Demand-based: Only dispatch if someone is listening (e.g., ObjectEventDispatcher)
    if (!skipDispatch && result.status === 'new' && versionedObjEvent.hasListeners()) {
        console.log(`[STORAGE-EVENT] Dispatching onVersionedObj (NoMerge): ${result.obj.$type$} hash=${String(result.hash).substring(0, 8)}`);
        versionedObjEvent.dispatch(result);
    }
    return result;
}
/**
 * Store a versioned object without version tree management or merging.
 *
 * This function stores the object data and creates the necessary version nodes but does not
 * perform any merging with existing versions. It's primarily used internally by other storage
 * functions when you want to bypass the merge logic.
 *
 * **Important**: This function does not handle version conflicts or merging. If you need
 * conflict resolution, use `storeVersionedObject` with appropriate `storeAs` parameter instead.
 *
 * @example
 * ```typescript
 * // Store an object without merge logic
 * const result = await storeVersionedObjectNoMerge({
 *     $type$: 'Person',
 *     name: 'John Doe',
 *     email: 'john@example.com'
 * });
 *
 * // The result includes a timestamp
 * console.log('Stored at:', new Date(result.timestamp));
 * ```
 *
 * @example
 * ```typescript
 * // When storing the same object twice, it returns the existing one
 * const obj = { $type$: 'Group', name: 'Developers' };
 * const first = await storeVersionedObjectNoMerge(obj);
 * const second = await storeVersionedObjectNoMerge(obj);
 *
 * // Both results have the same hash
 * console.log(first.hash === second.hash); // true
 * ```
 *
 * @async
 * @param {OneVersionedObjectTypes} obj - A complete versioned ONE object to store.
 * @returns {Promise<VersionedObjectResult & {timestamp: number}>} Resolves with the crypto
 * hashes, metadata, and a guaranteed timestamp. If the exact same object already exists,
 * returns the existing result with a timestamp.
 */
export async function storeVersionedObjectNoMerge(obj, skipDispatch = false) {
    if (!isVersionedObject(obj)) {
        throw createError('SVO-SO2', { obj });
    }
    const microdata = convertObjToMicrodata(obj);
    const hash = await createCryptoHash(microdata);
    const idHash = await calculateIdHashOfObj(obj);
    // Check if current version is the same as the object being stored
    try {
        const currentVersionHash = await getCurrentVersionHash(idHash);
        if (currentVersionHash === hash) {
            // Return existing object if it's the same
            const existingResult = await getObjectByIdHash(idHash);
            return {
                ...existingResult,
                timestamp: existingResult.timestamp ?? Date.now()
            };
        }
    }
    catch (e) {
        // If no current version exists, continue with normal storage
        if (e.name !== 'FileNotFoundError') {
            throw e;
        }
    }
    return storeVersionedObjectWithMicrodataNoMerge(obj, microdata, hash, skipDispatch);
}
/**
 * Store a change versioned object that represents a sequential change from the current version.
 *
 * **Version Tree Behavior:**
 * - If a current version exists: Creates a `VersionNodeChange` pointing to the current version
 * - If no current version exists: Creates a `VersionNodeEdge` as the initial version
 *
 * This function is typically used for local modifications that build upon the current version
 * of an object. It automatically handles version tree updates and merges the change with the
 * current state using CRDT algorithms when necessary.
 *
 * @example
 * ```typescript
 * // Store a local change to an existing object
 * const result = await storeVersionObjectAsChange({
 *     $type$: 'Person',
 *     name: 'John Doe',
 *     email: 'john.doe@example.com'
 * });
 *
 * // The result contains the merged state if conflicts were resolved
 * console.log('Updated person:', result.obj.name);
 * ```
 * This will result in a VersionNodeChange being created, pointing to the current version
 * or a VersionNodeEdge if no current version is available.
 *
 * @example
 * ```typescript
 * // Creating the first version of an object
 * const firstVersion = await storeVersionObjectAsChange({
 *     $type$: 'Group',
 *     name: 'Development Team'
 * });
 * // This creates a VersionNodeEdge since no current version exists
 *
 * // Adding a change to the existing object
 * const updatedVersion = await storeVersionObjectAsChange({
 *     $type$: 'Group',
 *     name: 'Senior Development Team'
 * });
 * // This creates a VersionNodeChange pointing to the previous version
 * ```
 *
 * @async
 * @param {OneVersionedObjectTypes} obj - A versioned ONE object representing a local change.
 * @returns {Promise<VersionedObjectResult>} Resolves with the crypto hashes and metadata.
 * The result may contain a merged version if conflicts were resolved during storage.
 */
export async function storeVersionObjectAsChange(obj) {
    if (!isVersionedObject(obj)) {
        throw createError('SVO-SO2', { obj });
    }
    const microdata = convertObjToMicrodata(obj);
    const hash = await createCryptoHash(microdata);
    const idHash = await calculateIdHashOfObj(obj);
    // Check if current version is the same as the object being stored
    try {
        const currentVersionHash = await getCurrentVersionHash(idHash);
        if (currentVersionHash === hash) {
            // Return existing object if it's the same
            return await getObjectByIdHash(idHash);
        }
    }
    catch (e) {
        // If no current version exists, continue with normal storage
        if (e.name !== 'FileNotFoundError') {
            throw e;
        }
    }
    // Skip dispatch here - mergeVersionAsChangeWithCurrent handles dispatch after merge
    const dataResult = await storeVersionedObjectWithMicrodataNoMerge(obj, microdata, hash, true);
    return mergeVersionAsChangeWithCurrent(dataResult);
}
/**
 * Store a merge versioned object that represents a new version tree to be merged.
 *
 * **Version Tree Behavior:**
 * - If a current version exists: Creates a `VersionNodeEdge` for the new object, then a
 *   `VersionNodeMerge` pointing to both the current version and the newly created edge
 * - If no current version exists: Creates a `VersionNodeEdge` as the initial version
 *
 * This function is typically used for objects received from remote sources (like sync operations)
 * that need to be merged with the current state. It handles the complexity of merging different
 * version trees and resolving conflicts using CRDT algorithms.
 *
 * @example
 * ```typescript
 * // Store a remote object that needs to be merged
 * const result = await storeVersionObjectAsMerge({
 *     $type$: 'Person',
 *     name: 'Jane Smith',
 *     email: 'jane.smith@example.com'
 * });
 *
 * // The result contains the merged state
 * console.log('Merged person:', result.obj.name);
 * ```
 *
 * @example
 * ```typescript
 * // Typical sync scenario - merging remote changes
 * const localVersion = await storeVersionObjectAsChange({
 *     $type$: 'Document',
 *     title: 'Local Changes',
 *     content: 'Local content'
 * });
 *
 * // Later, a remote version comes in that needs to be merged
 * const mergedResult = await storeVersionObjectAsMerge({
 *     $type$: 'Document',
 *     title: 'Remote Changes',
 *     content: 'Remote content'
 * });
 *
 * // The result contains the merged state of both local and remote changes
 * console.log('Final merged document:', mergedResult.obj);
 * ```
 *
 * @async
 * @param {OneVersionedObjectTypes} obj - A versioned ONE object from a remote source.
 * @returns {Promise<VersionedObjectResult>} Resolves with the crypto hashes and metadata.
 * The result contains the final merged state after conflict resolution.
 */
export async function storeVersionObjectAsMerge(obj) {
    if (!isVersionedObject(obj)) {
        throw createError('SVO-SO2', { obj });
    }
    const microdata = convertObjToMicrodata(obj);
    const hash = await createCryptoHash(microdata);
    const idHash = await calculateIdHashOfObj(obj);
    // Check if current version is the same as the object being stored
    try {
        const currentVersionHash = await getCurrentVersionHash(idHash);
        if (String(currentVersionHash) === String(hash)) {
            // Return existing object if it's the same
            return (await getObjectByIdHash(idHash));
        }
    }
    catch (e) {
        // If no current version exists, continue with normal storage
        if (e.name !== 'FileNotFoundError') {
            throw e;
        }
    }
    // Skip dispatch here - mergeVersionAsEdgeWithCurrent handles dispatch after merge
    const dataResult = await storeVersionedObjectWithMicrodataNoMerge(obj, microdata, hash, true);
    return mergeVersionAsEdgeWithCurrent(dataResult);
}
/**
 * Constants for specifying how to store versioned objects.
 *
 * @example
 * ```typescript
 * // Store as local change (default)
 * await storeVersionedObject(obj, STORE_AS.CHANGE);
 *
 * // Store as remote merge
 * await storeVersionedObject(obj, STORE_AS.MERGE);
 *
 * // Store without version tree management
 * await storeVersionedObject(obj, STORE_AS.NO_VERSION_MAP);
 * ```
 */
export const STORE_AS = {
    /**
     * Store as a local sequential change. Creates a VersionNodeChange that represents
     * a local modification building upon the current version. This is the default behavior.
     */
    CHANGE: 'change',
    /**
     * Store as a remote object to merge. Creates a VersionNodeEdge that represents
     * a version coming from a remote source that needs to be merged with the current state.
     */
    MERGE: 'merge',
    /**
     * Store without version tree management. The object is stored but no version
     * nodes or version tree updates are performed.
     */
    NO_VERSION_MAP: 'no-version-map'
};
export async function storeVersionedObject(obj, storeAs = STORE_AS.CHANGE) {
    if (storeAs === STORE_AS.CHANGE) {
        return storeVersionObjectAsChange(obj);
    }
    else if (storeAs === STORE_AS.MERGE) {
        return storeVersionObjectAsMerge(obj);
    }
    else {
        return storeVersionedObjectNoMerge(obj);
    }
}
/**
 * Returns the latest version of the object specified by the ID hash
 * or a rejected promise if there is no such object.
 *
 * @async
 * @param {SHA256IdHash} idHash - Hash of a ONE ID object
 * @returns {Promise<VersionedObjectResult>} The promise is rejected with an Error whose name
 * property is set to "FileNotFoundError" if the object does not exist, or with an Error if it
 * is of the wrong type.
 */
export async function getObjectByIdHash(idHash) {
    const versionNode = await getCurrentVersionNode(idHash);
    const data = await getObject(versionNode.obj.data);
    const dataMicrodata = convertObjToMicrodata(data);
    const dataHash = await createCryptoHash(dataMicrodata);
    return {
        obj: data,
        hash: dataHash,
        idHash,
        status: CREATION_STATUS.EXISTS,
        timestamp: versionNode.obj.creationTime
    };
}
/**
 * Frontend for getObjectByIdHash() that accepts an ID object and calculates its crypto hash
 * before calling that function. Returns the latest version of the object specified by the
 * ID hash, or a rejected promise if there is no such object.
 *
 * @async
 * @param {(OneVersionedObjectTypes)} obj - A versioned ONE object of type
 * `<T>` which is used to create its ID hash. Note that it does not have to be an ID object,
 * i.e. it can have more properties than just ID properties, they will be ignored.
 * @returns {Promise<VersionedObjectResult<OneVersionedObjectTypes>>} The promise is rejected with an Error whose name
 * property is set to "FileNotFoundError" if the object does not exist.
 */
export async function getObjectByIdObj(obj) {
    const idHash = await calculateIdHashOfObj(obj);
    return getObjectByIdHash(idHash);
}
export const MERGE_AS = {
    REMOTE: 'edge',
    LOCAL: 'change'
};
/**
 * Merge a version with the current storage
 *
 * @internal
 * @async
 * @param {VersionedObjectResult<T>} mergeDataResult - Data to merge
 * @param {'edge' | 'change'} as - If 'edge', creates VersionNodeEdge; if 'change', creates VersionNodeChange
 * @returns {Promise<VersionedObjectResult<T>>}
 */
export async function mergeVersionWithCurrent(mergeDataResult, mergeAs) {
    let currentNode;
    const results = await serializeWithType(`VersionMerge${mergeDataResult.idHash}`, async () => {
        try {
            currentNode = await getCurrentVersionNode(mergeDataResult.idHash);
        }
        catch (e) {
            if (e.name !== 'FileNotFoundError') {
                throw e;
            }
            // EXIT CONDITION 1: If no current version exists => Set the current version as edge
            const versionNode = await storeVersionNode(mergeDataResult, true);
            await writeUTF8SystemMapFile(versionNode.hash, mergeDataResult.idHash, STORAGE.VHEADS);
            return { dataResult: mergeDataResult, new: true };
        }
        // Create version node based on as parameter
        const mergeNode = await storeVersionNode(mergeDataResult, mergeAs === MERGE_AS.REMOTE);
        if (currentNode.hash === mergeNode.hash) {
            // EXIT CONDITION 2: to-merge and current version are the same => just return
            return { dataResult: mergeDataResult, new: false };
        }
        const mergeResult = await mergeObjects(currentNode.hash, mergeNode.hash, mergeDataResult.obj.$type$);
        if (mergeResult.alreadyMerged) {
            if (mergeResult.newNodeHash === currentNode.hash) {
                // EXIT CONDITION 3: The current version already contained the to-merge
                // version => do nothing
                return {
                    dataResult: mergeResult.result,
                    new: false
                };
            }
            // EXIT CONDITION 4: The to-merge version already contained the current
            // version => set the to-merge version as current version
            mergeResult.result.status = 'new';
            await writeUTF8SystemMapFile(mergeResult.newNodeHash, mergeDataResult.idHash, STORAGE.VHEADS);
            return {
                dataResult: mergeResult.result,
                new: true
            };
        }
        // If we are here, a real merge happened and we need to calculate the new node. This can
        // either be a new merge node, or probably one of the existing change nodes
        // Compute the new node children
        const nodes = new Set([
            ...(currentNode.obj.$type$ === 'VersionNodeEdge'
                ? [currentNode.hash]
                : [currentNode.hash]),
            ...(mergeNode.obj.$type$ === 'VersionNodeEdge'
                ? [mergeNode.hash]
                : [mergeNode.hash])
        ]);
        // Filter the new node children that are also covered by another node children
        // be being a predecessor to another one. This prevents redundant edges in the graph.
        // Sort them, so that all instances will get the same result.
        let remainingNodeHashes;
        let depth;
        let creationTime;
        {
            const tree = mergeResult.tree;
            const treeNodes = [...nodes].map(n => tree.nodeByHash(n));
            const remainingNodes = treeNodes.filter(node => tree.findSucceedingNodesChangeOrRootOnly(node, n => n.hash === node.hash ? false : nodes.has(n.hash)).length === 0);
            remainingNodeHashes = remainingNodes.map(n => n.hash);
            depth = Math.max(...remainingNodes.map(n => n.depth)) + 1;
            creationTime = Math.max(...remainingNodes.map(n => n.obj.creationTime));
            remainingNodeHashes.sort();
        }
        // Write / compute new node
        let newNodeHash;
        if (remainingNodeHashes.length > 1) {
            newNodeHash = (await storeUnversionedObject({
                $type$: 'VersionNodeMerge',
                depth,
                creationTime,
                data: mergeResult.result.hash,
                nodes: new Set(remainingNodeHashes)
            })).hash;
            await writeUTF8SystemMapFile(newNodeHash, mergeResult.result.idHash, STORAGE.VHEADS);
        }
        else {
            newNodeHash = remainingNodeHashes[0];
        }
        // EXIT CONDITION 5: A proper merge was done
        await writeUTF8TextFile(newNodeHash, mergeDataResult.idHash, STORAGE.VHEADS);
        return { dataResult: mergeResult.result, new: true };
    });
    // Dispatch outside of serializeWithType - object is immutable, status already set during storage
    // CRITICAL: Override status to 'new' when results.new is true, even if original status was 'exists'
    // This ensures ObjectEventDispatcher processes CHUM imports (which pass status: 'exists')
    if (results.new) {
        versionedObjEvent.dispatch({
            ...results.dataResult,
            status: 'new'
        });
    }
    return results.dataResult;
}
/**
 * Merge a local change version with the current storage (creates VersionNodeChange)
 *
 * @internal
 * @async
 * @param {VersionedObjectResult<T>} mergeDataResult - Data to merge as a local change
 * @returns {Promise<VersionedObjectResult<T>>}
 */
export async function mergeVersionAsChangeWithCurrent(mergeDataResult) {
    return mergeVersionWithCurrent(mergeDataResult, MERGE_AS.LOCAL);
}
/**
 * Merge a remote version with the current storage (creates VersionNodeEdge)
 *
 * @internal
 * @async
 * @param {VersionedObjectResult<T>} mergeDataResult - Data to merge as a remote object
 * @returns {Promise<VersionedObjectResult<T>>}
 */
export async function mergeVersionAsEdgeWithCurrent(mergeDataResult) {
    return mergeVersionWithCurrent(mergeDataResult, MERGE_AS.REMOTE);
}
/**
 * Get the current VersionNode
 *
 * @async
 * @param {SHA256IdHash<T>} idHash
 * @returns {Promise<VersionNode<T>>}
 * @throws {Error} Throws an Error whose name property is set to `FileNotFoundError` if the file cannot be found
 */
export async function getCurrentVersionNode(idHash) {
    const versionHeadNodeHash = await readUTF8TextFile(idHash, STORAGE.VHEADS);
    const versionHeadNodeObj = await getObject(ensureHash(versionHeadNodeHash));
    const objMicrodata = convertObjToMicrodata(versionHeadNodeObj);
    const objHash = await createCryptoHash(objMicrodata);
    return {
        obj: versionHeadNodeObj,
        hash: objHash,
        status: CREATION_STATUS.EXISTS
    };
}
/**
 * Get the version of the object
 *
 * @async
 * @param {SHA256Hash<VersionNode<T>> | SHA256Hash<T> | VersionNode<T> | UnversionedObjectResult<VersionNode<T>>} hashOrNode - The hash of the version node or the version node itself
 * @returns {Promise<T>}
 */
export async function getVersion(hashOrNode) {
    if (isHash(hashOrNode)) {
        const obj = await getObject(hashOrNode);
        if (!isVersionNode(obj)) {
            return obj;
        }
        // is version node
        const versionNode = obj;
        return await getObject(versionNode.data);
    }
    if (isUnversionedObjectResult(hashOrNode)) {
        return await getObject(hashOrNode.obj.data);
    }
    return await getObject(hashOrNode.data);
}
/**
 * Get the current version hash of the object
 *
 * @async
 * @param {SHA256IdHash<T>} idHash - The hash of the id-object
 * @returns {Promise<SHA256Hash<T>>}
 */
export async function getCurrentVersionHash(idHash) {
    const versionNode = await getCurrentVersionNode(idHash);
    return versionNode.obj.data;
}
/**
 * Get the version hash of the object
 *
 * @async
 * @param {SHA256Hash<VersionNode<T>> | VersionNode<T> | UnversionedObjectResult<VersionNode<T>>} hashOrNode - The hash of the version node or the version node itself
 * @returns {Promise<SHA256Hash<T>>}
 */
export async function getVersionHash(hashOrNode) {
    return calculateHashOfObj(await getVersion(hashOrNode));
}
/**
 * Get the current version of the object
 *
 * @async
 * @param {SHA256IdHash<T>} idHash - The hash of the id-object
 * @returns {Promise<T>}
 */
export async function getCurrentVersion(idHash) {
    const versionNode = await getCurrentVersionNode(idHash);
    const obj = await getObject(versionNode.obj.data);
    return obj;
}
/**
 * Get a VersionNode
 *
 * @async
 * @param {SHA256Hash<VersionNode<T>>} hash
 * @returns {Promise<VersionNode<T>>}
 * @throws {Error} Throws an Error whose name property is set to `FileNotFoundError` if the file cannot be found
 */
export async function getVersionNodeByNodeHash(hash) {
    const obj = await getObject(hash);
    const objMicrodata = convertObjToMicrodata(obj);
    const objHash = await createCryptoHash(objMicrodata);
    return {
        obj,
        hash: objHash,
        status: CREATION_STATUS.EXISTS
    };
}
/**
 * Get a VersionNode
 *
 * @async
 * @param {SHA256Hash<T>} hash
 * @returns {Promise<VersionNode<T>>}
 * @throws {Error} Throws an Error whose name property is set to `FileNotFoundError` if the file cannot be found
 */
export async function getVersionNodeByDataHash(idHash, hash) {
    const versionNode = await getCurrentVersionNode(idHash);
    if (versionNode.obj.data === hash) {
        return versionNode;
    }
    const versionNodeHash = await _getVersionNodeHashForDataHash(versionNode.hash, hash);
    if (versionNodeHash === undefined) {
        return undefined;
    }
    return await getVersionNodeByNodeHash(versionNodeHash);
}
/**
 * Save the version object as VersionNodeChange
 *
 * @internal
 * @async
 * @param {VersionedObjectResult<T>} version - the last version of the object
 * @param {boolean} [edge=false] - Optional. Default: false. skips the previous VersionNode check and saves the object as edge of the version tree
 * @returns {Promise<UnversionedObjectResult<VersionNodeChange<T>>>}
 */
async function storeVersionNode(version, edge = false) {
    if (edge) {
        return await storeVersionNodeEdge(version);
    }
    try {
        const previousVersionNode = await getCurrentVersionNode(version.idHash);
        return storeVersionNodeChange(version, previousVersionNode);
    }
    catch (e) {
        if (e.name !== 'FileNotFoundError') {
            throw e;
        }
        return await storeVersionNodeEdge(version);
    }
}
/**
 * Save the version object as VersionNodeChange
 *
 * @internal
 * @async
 * @param {VersionedObjectResult<T>} version - the last version of the object
 * @param {UnversionedObjectResult<VersionNode<T>>} previousVersionNode - the previous version node
 * @returns {Promise<UnversionedObjectResult<VersionNodeChange<T>>>}
 */
export async function storeVersionNodeChange(version, previousVersionNode) {
    return await storeUnversionedObject({
        $type$: 'VersionNodeChange',
        depth: previousVersionNode.obj.depth + 1,
        data: version.hash,
        prev: previousVersionNode.hash,
        creationTime: version.timestamp ?? Date.now()
    });
}
/**
 * Save the version object as VersionNodeEdge
 *
 * @internal
 * @async
 * @param {VersionedObjectResult<T>} version - the last version of the object
 * @returns {Promise<UnversionedObjectResult<VersionNodeEdge<T>>>}
 */
export async function storeVersionNodeEdge(version) {
    return await storeUnversionedObject({
        $type$: 'VersionNodeEdge',
        depth: 0,
        data: version.hash,
        creationTime: version.timestamp ?? Date.now()
    });
}
/**
 * Get the last node from an array of nodes
 *
 * @param {Array<VersionNode<T>>} nodes - The array of nodes
 * @returns {VersionNode<T>} The last node
 */
export function getLastNodeFromArray(nodes) {
    return nodes.reduce((maxDepthNode, node) => (node.depth > maxDepthNode.depth ? node : maxDepthNode), nodes[0]);
}
/**
 * Get the versions nodes of the version node and all its predecessors
 *
 * @async
 * @param {SHA256IdHash<T>} idHash - The hash of the id-object
 * @returns {Promise<Array<VersionNode<T>>>}
 */
export async function getVersionsNodes(idHash) {
    const versionsNodeHashes = await getVersionsNodeHashes(idHash);
    if (versionsNodeHashes === undefined) {
        throw new Error('No versions node hashes found');
    }
    return Promise.all(versionsNodeHashes.map(async (hash) => {
        return (await getVersionNodeByNodeHash(hash)).obj;
    }));
}
/**
 * Get the versions hashes of the version node and all its predecessors
 *
 * @async
 * @param {SHA256IdHash<T>} idHash - The hash of the id-object
 * @param {T} type - The type of the object
 * @returns {Promise<Array<SHA256Hash<T>>>}
 */
export async function getVersionsHashes(idHash) {
    return (await getVersionsNodes(idHash)).map(node => node.data);
}
/**
 * Get the versions node hashes of the version node and all its predecessors
 *
 * @async
 * @param {SHA256IdHash<T>} idHash - The hash of the id-object
 * @param {SHA256Hash<VersionNode<T>>} [beforeVersionNodeHash] - Optional. The hash of the version node above which the version hashes should not be included
 * @returns {Promise<Array<SHA256Hash<VersionNode<T>>> | undefined>}
 */
export async function getVersionsNodeHashes(idHash, beforeVersionNodeHash) {
    if (!(await exists(idHash, STORAGE.VHEADS))) {
        return undefined;
    }
    const versionNode = await getCurrentVersionNode(idHash);
    return (await _getVersionsNodeHashes(versionNode.hash, beforeVersionNodeHash)).reverse();
}
/**
 * Get the versions node hashes of the version node and all its predecessors
 *
 * @internal
 * @async
 * @param {SHA256Hash<VersionNode<T>>} versionNodeHash - The hash of the version node
 * @param {SHA256Hash<VersionNode<T>>} [beforeVersionNodeHash] - Optional. The hash of the version node above which the version hashes should not be included
 * @returns {Promise<Array<SHA256Hash<VersionNode<T>>>>}
 */
async function _getVersionsNodeHashes(versionNodeHash, beforeVersionNodeHash) {
    if (versionNodeHash === beforeVersionNodeHash) {
        return [];
    }
    const node = await getObjectWithType(ensureHash(versionNodeHash), [
        'VersionNodeMerge',
        'VersionNodeChange',
        'VersionNodeEdge'
    ]);
    switch (node.$type$) {
        case 'VersionNodeEdge': {
            return [versionNodeHash];
        }
        case 'VersionNodeChange': {
            if (node.prev !== undefined) {
                return [
                    versionNodeHash,
                    ...(await _getVersionsNodeHashes(node.prev, beforeVersionNodeHash))
                ];
            }
            return [versionNodeHash];
        }
        case 'VersionNodeMerge': {
            const hashes = [];
            for (const nodeHash of node.nodes) {
                hashes.push(...(await _getVersionsNodeHashes(nodeHash, beforeVersionNodeHash)));
            }
            return hashes;
        }
        default: {
            throw new Error('Invalid version node type');
        }
    }
}
/**
 * Get the versions node hashes of the version node and all its predecessors
 *
 * @internal
 * @async
 * @param {SHA256Hash<VersionNode<T>>} versionNodeHash - The hash of the version node
 * @returns {Promise<Array<SHA256Hash<VersionNode<T>>>>}
 */
async function _getVersionNodeHashForDataHash(versionNodeHash, dataHash) {
    const node = await getObjectWithType(ensureHash(versionNodeHash), [
        'VersionNodeMerge',
        'VersionNodeChange',
        'VersionNodeEdge'
    ]);
    switch (node.$type$) {
        case 'VersionNodeEdge': {
            if (node.data === dataHash) {
                return versionNodeHash;
            }
            return undefined;
        }
        case 'VersionNodeChange': {
            if (node.data === dataHash) {
                return versionNodeHash;
            }
            return await _getVersionNodeHashForDataHash(node.prev, dataHash);
        }
        case 'VersionNodeMerge': {
            for (const nodeHash of node.nodes) {
                const hash = await _getVersionNodeHashForDataHash(nodeHash, dataHash);
                if (hash !== undefined) {
                    return hash;
                }
            }
            return undefined;
        }
        default: {
            throw new Error('Invalid version node type');
        }
    }
}
//# sourceMappingURL=storage-versioned-objects.js.map